import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";
import { useCart, ZAR } from "@/lib/cart";
import usePageMeta, { buildProductSchema, buildBreadcrumbSchema } from "@/lib/useSeo";
import WishlistButton from "@/components/WishlistButton";
import ReviewsSection from "@/components/ReviewsSection";
import FrequentlyBoughtWith from "@/components/FrequentlyBoughtWith";
import ShareButtons from "@/components/ShareButtons";
import StarRating from "@/components/StarRating";
import { PRODUCT } from "@/constants/testIds";
import { CaretLeft, Plus, Minus } from "@phosphor-icons/react";

const swatchColorFor = (name) => {
  const s = (name || "").toLowerCase();
  if (s.includes("mint")) return "#00FFC2";
  if (s.includes("cool") || s.includes("ice") || s.includes("menthol")) return "#7CE9FF";
  if (s.includes("berry") || s.includes("razz") || s.includes("blueberry")) return "#8B5CF6";
  if (s.includes("strawberry") || s.includes("watermelon") || s.includes("cherry")) return "#FF5A1F";
  if (s.includes("peach") || s.includes("mango")) return "#FFA340";
  if (s.includes("grape")) return "#B94BFF";
  if (s.includes("cola")) return "#8B4513";
  if (s.includes("apple")) return "#22C55E";
  if (s.includes("lemon") || s.includes("banana")) return "#FFC900";
  if (s.includes("pink")) return "#FF5A1F";
  if (s.includes("cyan") || s.includes("blue")) return "#00FFC2";
  if (s.includes("obsidian") || s.includes("black")) return "#1a1a1a";
  return "#525252";
};

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [selected, setSelected] = useState({});
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);
  const [summary, setSummary] = useState({ average: 0, count: 0 });
  const cart = useCart();

  useEffect(() => {
    api.get(`/reviews/${slug}`).then((r) =>
      setSummary({ average: r.data.average, count: r.data.count })
    );
  }, [slug]);

  // Structured data for Google Shopping / rich results
  useEffect(() => {
    if (!product) return;
    let sd = document.getElementById("ld-product");
    if (!sd) {
      sd = document.createElement("script");
      sd.type = "application/ld+json";
      sd.id = "ld-product";
      document.head.appendChild(sd);
    }
    sd.textContent = JSON.stringify({
      "@context": "https://schema.org/",
      "@type": "Product",
      name: product.name,
      description: product.description,
      brand: { "@type": "Brand", name: product.brand },
      image: (product.images || []).map((i) => (i.startsWith("http") ? i : `${window.location.origin}${i}`)),
      offers: {
        "@type": "Offer",
        priceCurrency: "ZAR",
        price: product.price,
        availability:
          (product.stock || 0) > 0
            ? "https://schema.org/InStock"
            : "https://schema.org/OutOfStock",
        url: window.location.href,
      },
      ...(summary.count > 0 && {
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: summary.average,
          reviewCount: summary.count,
        },
      }),
    });
    return () => {
      // keep the tag in place across product switches (updated in-place)
    };
  }, [product, summary]);

  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setNotFound(false);
    api
      .get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        const initial = {};
        (r.data.variant_options || []).forEach((o) => (initial[o.name] = o.values?.[0]));
        setSelected(initial);
        setImgIdx(0);
      })
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
      });
  }, [slug]);

  usePageMeta({
    title: product
      ? `${product.name} — ${product.brand} · Vape Essence`
      : "Vape Essence",
    description: product
      ? `Buy ${product.name} by ${product.brand} from R${product.price?.toFixed(0)}. ${(product.description || "").slice(0, 120)} 48-72hr delivery in South Africa.`
      : "Premium vapes & nicotine pouches — Vape Essence.",
    image: product?.images?.[0] ? mediaUrl(product.images[0]) : undefined,
    type: "product",
    jsonLd: product
      ? [
          buildProductSchema({
            product,
            reviewSummary: summary,
            url: `https://vapeessence.co.za/product/${product.slug}`,
          }),
          buildBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Shop", url: "/shop" },
            { name: product.category, url: `/shop?cat=${encodeURIComponent(product.category)}` },
            { name: product.name, url: `/product/${product.slug}` },
          ]),
        ]
      : undefined,
  });

  const canAdd = useMemo(() => {
    if (!product) return false;
    return (product.variant_options || []).every((o) => selected[o.name]);
  }, [product, selected]);

  const addToCart = () => {
    if (!product) return;
    cart.add({
      product_slug: product.slug,
      name: product.name,
      unit_price: product.price,
      quantity: qty,
      variant: Object.keys(selected).length ? selected : null,
      image: product.images?.[0] || null,
    });
    toast.success("Added to cart", {
      description: `${product.name} × ${qty}`,
    });
  };

  if (notFound) {
    return (
      <div className="mx-auto max-w-[720px] px-4 py-24 text-center sm:px-6 sm:py-40 lg:px-10">
        <div className="font-label text-xs text-[#FF5A1F]">/ 404 · PRODUCT</div>
        <h1 className="mt-3 font-display text-3xl font-black tracking-tighter sm:text-5xl">
          This product doesn't exist.
        </h1>
        <p className="mt-4 text-sm text-white/60">
          It may have been renamed or removed. Head back to the shop to keep browsing.
        </p>
        <Link
          to="/shop"
          className="mt-8 inline-flex items-center gap-2 bg-[#FF5A1F] px-6 py-3 font-label text-xs text-black hover:-translate-y-0.5 transition-transform"
        >
          Back to shop
        </Link>
      </div>
    );
  }

  if (!product) {
    return <div className="p-20 text-center text-white/40">Loading...</div>;
  }

  return (
    <div className="mx-auto max-w-[1200px] px-4 py-10 sm:px-6 sm:py-12 lg:px-10">
      <Link
        to="/shop"
        className="inline-flex items-center gap-2 font-label text-xs text-white/50 hover:text-white transition-colors"
      >
        <CaretLeft size={14} weight="bold" /> Back to Shop
      </Link>

      <div className="mt-6 grid gap-8 md:grid-cols-2 md:gap-10">
        {/* Gallery */}
        <div data-testid={PRODUCT.gallery} className="space-y-3 md:sticky md:top-24 md:self-start">
          <motion.div
            key={imgIdx}
            initial={{ opacity: 0.6, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="relative mx-auto aspect-square w-full max-w-[420px] overflow-hidden border border-white/10 bg-gradient-to-b from-[#0d0d0d] to-black"
          >
            {product.images?.[imgIdx] ? (
              <img
                src={mediaUrl(product.images[imgIdx])}
                alt={product.name}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full items-center justify-center text-white/20">no image</div>
            )}
            <div className="absolute left-3 top-3 border border-white/15 bg-black/70 backdrop-blur px-2 py-1 font-label text-[10px] text-white/70">
              {product.category}
            </div>
          </motion.div>
          {product.images?.length > 1 && (
            <div className="mx-auto flex max-w-[420px] gap-2 overflow-x-auto">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setImgIdx(i)}
                  className={`h-14 w-14 flex-shrink-0 overflow-hidden border transition-colors ${
                    i === imgIdx ? "border-[#FF5A1F]" : "border-white/15"
                  }`}
                >
                  <img src={mediaUrl(img)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="font-label text-[10px] text-[#FF5A1F]">/ {product.brand}</div>
          <h1 className="mt-2 font-display text-2xl font-black tracking-tighter sm:text-3xl">
            {product.name}
          </h1>
          <div className="mt-3 flex items-center gap-3">
            <StarRating value={summary.average} count={summary.count} size={14} />
            {product.stock > 0 && product.stock <= 5 && (
              <span className="inline-flex items-center gap-1 border border-[#FF5A1F] px-2 py-1 font-label text-[9px] text-[#FF5A1F] animate-pulse">
                🔥 ONLY {product.stock} LEFT
              </span>
            )}
          </div>

          <div className="mt-5 flex items-baseline gap-3">
            <div data-testid={PRODUCT.price} className="font-display text-3xl font-black text-[#00FFC2]">
              {ZAR(product.price)}
            </div>
            {product.compare_at && product.compare_at > product.price && (
              <div className="text-base text-white/40 line-through">{ZAR(product.compare_at)}</div>
            )}
          </div>

          <p className="mt-5 text-sm leading-relaxed text-white/70">{product.description}</p>

          <div className="mt-3 text-[11px] font-label text-[#FFC900]">
            + Earn {Math.max(0, Math.round((product.price * qty / 1.15) * 0.05))} loyalty points (5% ex-VAT) on this order
          </div>

          {/* Variants */}
          {(product.variant_options || []).map((opt) => (
            <div key={opt.name} className="mt-6">
              <div className="font-label text-[10px] text-white/50 mb-2">
                {opt.name}: <span className="text-white">{selected[opt.name]}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {opt.values.map((v) => {
                  const isSelected = selected[opt.name] === v;
                  const isColorOpt =
                    opt.name.toLowerCase().includes("colour") ||
                    opt.name.toLowerCase().includes("color") ||
                    opt.name.toLowerCase().includes("flavour") ||
                    opt.name.toLowerCase().includes("flavor");
                  return (
                    <button
                      key={v}
                      data-testid={PRODUCT.variantOption}
                      onClick={() => setSelected({ ...selected, [opt.name]: v })}
                      className={`group inline-flex items-center gap-2 border px-3 py-1.5 font-label text-[11px] transition-all ${
                        isSelected
                          ? "border-[#FF5A1F] bg-[#FF5A1F]/10 text-[#FF5A1F]"
                          : "border-white/15 hover:border-white/40"
                      }`}
                    >
                      {isColorOpt && (
                        <span
                          className="h-3 w-3 rounded-full border border-white/30"
                          style={{ background: swatchColorFor(v) }}
                        />
                      )}
                      {v}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Qty + Add */}
          <div className="mt-8 flex flex-wrap gap-3">
            <div className="flex items-center border border-white/15">
              <button
                onClick={() => setQty(Math.max(1, qty - 1))}
                className="px-3 py-2.5 hover:bg-white/5 transition-colors"
              >
                <Minus size={12} />
              </button>
              <input
                data-testid={PRODUCT.qtyInput}
                type="number"
                min={1}
                value={qty}
                onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))}
                className="w-12 bg-transparent text-center outline-none text-sm"
              />
              <button
                onClick={() => setQty(qty + 1)}
                className="px-3 py-2.5 hover:bg-white/5 transition-colors"
              >
                <Plus size={12} />
              </button>
            </div>
            <button
              data-testid={PRODUCT.addToCart}
              disabled={!canAdd}
              onClick={addToCart}
              className="flex-1 min-w-[180px] bg-[#FF5A1F] py-2.5 font-label text-[11px] text-black transition-transform hover:-translate-y-0.5 disabled:opacity-40"
            >
              Add to cart · {ZAR(product.price * qty)}
            </button>
            <WishlistButton productSlug={product.slug} />
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-3 text-[11px] text-white/50">
            <div className="border border-white/10 p-3">
              <div className="font-label text-[9px] text-[#00FFC2] mb-1">STOCK</div>
              {product.stock > 0 ? `${product.stock} in stock` : "Out of stock"}
            </div>
            <div className="border border-white/10 p-3">
              <div className="font-label text-[9px] text-[#00FFC2] mb-1">DELIVERY</div>
              48-72hr nationwide
            </div>
            <div className="border border-white/10 p-3">
              <div className="font-label text-[9px] text-[#00FFC2] mb-1">AUTHENTIC</div>
              Verified inventory
            </div>
          </div>

          <div className="mt-4">
            <ShareButtons
              url={`https://vapeessence.co.za/product/${product.slug}`}
              title={product.name}
              text={`${product.name} · from R${product.price?.toFixed(0)} on Vape Essence`}
              image={product.images?.[0] ? mediaUrl(product.images[0]) : undefined}
            />
          </div>
        </div>
      </div>

      <ReviewsSection productSlug={product.slug} />
      <FrequentlyBoughtWith slug={product.slug} />
    </div>
  );
}
